import { yupResolver } from '@hookform/resolvers/yup';
import { Groups } from '@mui/icons-material';
import { Box, DialogActions, DialogContent, Grid } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  IClientSettings,
  createNewOrganizationalUnitSettings,
  getAllOrganizationalUnitSettings,
  IOrganizationalUnit,
  getAllOrganizationalUnits,
} from 'wisestep-api';
import {
  WsButton,
  WsDialog,
  WsTypography,
  WsTypoVariants,
  WsColors,
  Item,
  ControlledTextInput,
  WsEditor,
  useWsEditor,
  WsSelect,
} from 'wisestep-base';
import { object, string } from 'yup';

import { useWorkspaceContext } from '../../../context';
import { useAtsCustomAPIAccess, AtsCustomAPIAccess } from '../../../hooks/useAtsCustomAPIAccess';
import { ClientAction, useClientAuthz } from '../../../hooks/useClientAuthz';
import { warning, success, failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { OrganizationalUnitDefaultPayload } from '../helper/ClientSettingsHelper';
import { OrganizationalScopeType } from '../types';

interface IOrganizationalUnitProps {
  ouOpenDialog: boolean;
  organizationalUnitValue?: IOrganizationalUnit;
  dataCallback(data: IClientSettings): void;
  onEdit: number;
  jobId?: string;
  oncloseDialog?: any;
  setOrganizationalUnitsState?: any;
  scopeType?: OrganizationalScopeType;
}
const validationSchema = object().shape({
  name: string().required('Please enter Organizational unit'),
});

const AddOrganizationalUnitDialog = ({
  ouOpenDialog,
  organizationalUnitValue,
  oncloseDialog,
  onEdit,
  dataCallback,
  setOrganizationalUnitsState,
  scopeType,
}: IOrganizationalUnitProps) => {
  const isAuthorizedToAddClient = useClientAuthz(ClientAction.ADD_CLIENT_UNIT);
  const isAuthorizedToAddClientContact = useClientAuthz(ClientAction.ADD_CLIENT_CONTACT_UNIT);
  const workspaceContext = useWorkspaceContext();
  const isAdmin = workspaceContext.owns;
  const addScopeTypes = [
    ...(isAdmin ? [{ label: 'Teams', value: 'Teams' }] : []),
    ...(isAdmin ? [{ label: 'Account Managers', value: 'Account Managers' }] : []),
    ...(isAuthorizedToAddClient ? [{ label: 'Clients', value: 'Clients' }] : []),
    ...(isAuthorizedToAddClientContact
      ? [{ label: 'Client Contacts', value: 'Client Contacts' }]
      : []),
  ];
  const formOptions = {
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: organizationalUnitValue?.name ?? '',
      description: isEmpty(organizationalUnitValue?.description)
        ? ''
        : organizationalUnitValue?.description,
    },
  };
  const methods = useForm(formOptions);
  const { setValue, reset } = methods;
  const navigate = useNavigate();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const onDescUpdate = (args: string | null) => {
    setValue('description', args ? args : '');
  };
  const editor = useWsEditor(organizationalUnitValue?.description ?? '', onDescUpdate);
  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MANAGE_CLIENT_SETTINGS,
  ]);
  const manageAccess =
    workspaceContext.owns ||
    atsCustomAccessControl[0] ||
    isAuthorizedToAddClient ||
    isAuthorizedToAddClientContact;
  const onUpdate = (data) => {
    data.id = organizationalUnitValue?.id;
    data.active = true;
    dataCallback(data);
    reset();
  };
  const fetchData = async () => {
    const respose = scopeType
      ? await getAllOrganizationalUnits(scopeType)
      : await getAllOrganizationalUnitSettings();
    setOrganizationalUnitsState(respose.data);
  };
  const onAddOrganizationalUnit = async (data) => {
    if (data?.name == '' || data?.description == '' || data?.addScope == undefined) {
      setNotification(warning('Please enter all fields'));
      return;
    }
    oncloseDialog();
    if (!manageAccess) {
      setNotification(warning('You do not have enough permissions to perform this action'));
      return;
    }
    const payload: any = { ...OrganizationalUnitDefaultPayload };
    payload.name = data?.name;
    payload.description = data?.description ?? '';
    payload.scopeType = data?.addScope ?? '';
    try {
      if (payload.name) {
        const response = await createNewOrganizationalUnitSettings(payload);
        setNotification(success(`${data?.name} has been added successfully !!`));
        fetchData();
        navigate('/organizational_unit/user-view', {
          state: {
            organizationalUnitDescription: response.data.description,
            organizationalUnitName: response.data.name,
            organizationalUnitId: response.data.id,
            organizationalUnitScopeType: response.data.scopeType,
            organizationalUnitOwner: response.data.createdBy,
            isSettingsPage: true,
            defaultIndex: 2,
            isNewUnit: true,
          },
        });
      } else setNotification(failed('Please input the name again !!'));
    } catch (error: any) {
      setNotification(failed('Something went wrong !!'));
    }
  };
  return (
    <FormProvider {...methods}>
      <WsDialog openDialog={ouOpenDialog} size="medium">
        <Grid container flexDirection="column">
          <DialogContent sx={{ width: 900 }}>
            <Item sx={{ pl: '45%', pr: '45%' }}>
              <Groups sx={{ color: WsColors.TheBlues400, fontSize: '60px' }} />
            </Item>
            <Item sx={{ margin: 'auto', py: 0, my: 0 }}>
              <WsTypography bold variant={WsTypoVariants.TITLE}>
                Enter a name for your Organizational Unit
              </WsTypography>
            </Item>

            <Item sx={{ margin: 'auto' }}>
              <ControlledTextInput
                name="name"
                placeholder="Enter Organizational Unit Name"
                required
                fullWidth
              />
            </Item>
            {onEdit != 1 && (
              <Item>
                <WsSelect
                  name="addScope"
                  label=""
                  selectArray={addScopeTypes}
                  placeholder="Add Scope Type"
                />
              </Item>
            )}
            <Item sx={{ margin: 'auto', py: 0, my: 0 }}>
              <WsTypography bold variant={WsTypoVariants.TITLE}>
                Description
              </WsTypography>
            </Item>
            <Item>
              <Box minHeight={100} border={1} borderColor={'gray'}>
                <WsEditor editor={editor} resizable={true} editorHeight={150}></WsEditor>
              </Box>
            </Item>
          </DialogContent>
          <DialogActions>
            <WsButton size="medium" variant="outlined" onClick={oncloseDialog}>
              Cancel
            </WsButton>
            {onEdit == 1 ? (
              <WsButton size="medium" variant="contained" onClick={methods.handleSubmit(onUpdate)}>
                Update
              </WsButton>
            ) : (
              <WsButton
                size="medium"
                variant="contained"
                onClick={methods.handleSubmit(onAddOrganizationalUnit)}
              >
                Create Unit
              </WsButton>
            )}
          </DialogActions>
        </Grid>
      </WsDialog>
    </FormProvider>
  );
};
export default AddOrganizationalUnitDialog;
